import React from 'react';
import { getSocket } from '../../services/realtimeService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';

interface CursorState { x: number; y: number; color: string; name: string; ts: number; pathname: string; }

const colors = ['#ef4444','#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899'];

function randomColor(seed: string){
  let h = 0; for(const c of seed) h = (h*33 + c.charCodeAt(0)) % colors.length; return colors[h];
}

function throttle<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let last = 0;
  let timeout: any = null;
  let lastArgs: any[] = [];
  const invoke = (ctx: any, args: any[]) => { last = Date.now(); fn.apply(ctx, args); };
  const throttled = function(this: any, ...args: any[]) {
    const now = Date.now();
    const remaining = wait - (now - last);
    lastArgs = args;
    if (remaining <= 0) {
      if (timeout) { clearTimeout(timeout); timeout = null; }
      invoke(this, args);
    } else if (!timeout) {
      timeout = setTimeout(() => { timeout = null; invoke(this, lastArgs); }, remaining);
    }
  } as any as T & { cancel?: () => void };
  (throttled as any).cancel = () => { if (timeout) { clearTimeout(timeout); timeout = null; } };
  return throttled;
}

const LiveCursorsOverlay: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [cursors, setCursors] = React.useState<Record<string, CursorState>>({});
  const [followUserId, setFollowUserId] = React.useState<string | null>(null);
  const [assistActive, setAssistActive] = React.useState<boolean>(false);
  const [remoteAssistPointer, setRemoteAssistPointer] = React.useState<{ visible: boolean; x: number; y: number } | null>(null);
  const myColor = React.useMemo(() => randomColor(user?.user_id || 'anon'), [user?.user_id]);

  React.useEffect(() => {
    const s = getSocket();
    const onUpdate = ({ fromUserId, x, y, nx, ny, name, pathname }: any) => {
      if (!fromUserId || fromUserId === user?.user_id) return;
      if (pathname !== location.pathname) return;
      const rx = (typeof nx === 'number') ? nx * window.innerWidth  : x;
      const ry = (typeof ny === 'number') ? ny * window.innerHeight : y;
      setCursors(prev => ({ ...prev, [fromUserId]: { x: rx || 0, y: ry || 0, color: randomColor(fromUserId), name, ts: Date.now(), pathname } }));
      if (followUserId === fromUserId) {
        try { window.scrollTo({ top: Math.max(0, (ry||0) - window.innerHeight/2), behavior: 'smooth' }); } catch {}
      }
    };
    s.on('cursor:update', onUpdate);
    // page-update: przenieś śledzącego do nowej ścieżki obserwowanego
    const onPage = ({ fromUserId, pathname, search }: any) => {
      if (fromUserId === user?.user_id) return;
      if (followUserId !== fromUserId) return;
      const target = `${pathname || '/'}${search || ''}`;
      const current = `${location.pathname}${location.search}`;
      if (target !== current) {
        navigate(target);
      }
    };
    s.on('page:update', onPage);
    // „Wybierz projekt” – jednolity sygnał niezależnie od drogi (hamburger/skrót/sidebar)
    const onProjectSelect = ({ fromUserId, projectId }: any) => {
      if (fromUserId === user?.user_id) return;
      if (followUserId !== fromUserId) return;
      try { window.dispatchEvent(new CustomEvent('ui:projects:select:external', { detail: { projectId } })); } catch {}
    };
    s.on('ui:projects:select', onProjectSelect);
    // Synchronizacja zakładek w ramach tej samej trasy
    const onTab = ({ fromUserId, page, tab }: any) => {
      if (fromUserId === user?.user_id) return;
      if (followUserId !== fromUserId) return;
      try {
        window.dispatchEvent(new CustomEvent('ui:tab:set:external', { detail: { page, tab } }));
      } catch {}
    };
    s.on('ui:tab:set', onTab);
    const onCheckbox = ({ fromUserId, path, checked, x, y }: any) => {
      if (fromUserId === user?.user_id) return;
      if (followUserId !== fromUserId) return;
      try {
        let el: HTMLInputElement | null = null;
        if (path) el = document.querySelector<HTMLInputElement>(path);
        if (!el && typeof x === 'number' && typeof y === 'number') {
          const at = document.elementFromPoint(x, y) as HTMLElement | null;
          const cand = at?.closest('input[type="checkbox"]') as HTMLInputElement | null;
          if (cand) el = cand;
        }
        if (el && el.type === 'checkbox') {
          el.checked = !!checked;
          const evt = new Event('change', { bubbles: true });
          el.dispatchEvent(evt);
        }
      } catch {}
    };
    s.on('dom:checkbox', onCheckbox);
    // Wskaźnik admina (odbiór po stronie użytkownika)
    const onAssist = ({ type, x, y, nx, ny }: any) => {
      const rx = (typeof nx === 'number') ? nx * window.innerWidth  : x;
      const ry = (typeof ny === 'number') ? ny * window.innerHeight : y;
      if (type === 'on') setRemoteAssistPointer({ visible: true, x: rx ?? 0, y: ry ?? 0 });
      if (type === 'move') setRemoteAssistPointer(prev => ({ visible: true, x: (rx ?? (prev?.x || 0)), y: (ry ?? (prev?.y || 0)) }));
      if (type === 'off') setRemoteAssistPointer(null);
    };
    s.on('assist:pointer', onAssist);

    return () => { s.off('cursor:update', onUpdate); s.off('page:update', onPage); s.off('ui:tab:set', onTab); s.off('dom:checkbox', onCheckbox); s.off('assist:pointer', onAssist); s.off('ui:projects:select', onProjectSelect); };
  }, [user?.user_id, location.pathname, followUserId]);

  React.useEffect(() => {
    const s = getSocket();
    const send = throttle((e: MouseEvent) => {
      const nx = e.clientX / window.innerWidth;
      const ny = e.clientY / window.innerHeight;
      s.emit('cursor:update', { nx, ny, name: user?.display_name || user?.email || 'Użytkownik', pathname: location.pathname });
    }, 50);
    window.addEventListener('mousemove', send);
    return () => { window.removeEventListener('mousemove', send as any); (send as any).cancel?.(); };
  }, [user?.display_name, user?.email, location.pathname]);

  // Emituj zmianę trasy dla współdzielenia nawigacji
  React.useEffect(() => {
    const s = getSocket();
    s.emit('page:update', { pathname: location.pathname, search: location.search });
  }, [location.pathname, location.search]);

  // Tryb obserwacji w nowej karcie: odczytaj ?follow=USER_ID i aktywuj bez klikania avatara
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const f = sp.get('follow');
      if (f) {
        setFollowUserId(f);
        try { sessionStorage.setItem('cobrowse:follow:userId', f); } catch {}
      }
    } catch {}
  }, []);

  // Wyjście z trybu: ESC
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFollowUserId(null);
        try { localStorage.removeItem('cobrowse:follow:userId'); } catch {}
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Subskrypcja: wybór użytkownika do śledzenia (tylko bieżąca karta gdy ktoś wyśle event – nie zapisujemy do localStorage)
  React.useEffect(() => {
    const onFollow = (e: any) => { const id = e?.detail?.userId || null; setFollowUserId(id); };
    window.addEventListener('cobrowse:follow', onFollow as any);
    return () => window.removeEventListener('cobrowse:follow', onFollow as any);
  }, []);

  // Auto-czyszczenie nieaktywnych kursorów
  React.useEffect(() => {
    const i = setInterval(() => {
      const now = Date.now();
      setCursors(prev => {
        const next: typeof prev = {}; Object.entries(prev).forEach(([k,v]) => { if (now - v.ts < 3000) next[k]=v; }); return next;
      });
    }, 2000);
    return () => clearInterval(i);
  }, []);

  const visibleEntries = React.useMemo(() => {
    const entries = Object.entries(cursors);
    if (!followUserId) return [] as [string, CursorState][];
    return entries.filter(([id]) => id === followUserId);
  }, [cursors, followUserId]);

  // === Helpery selectorów i wyszukiwania ===
  const getSelector = React.useCallback((el: Element | null): string => {
    if (!el || !(el as HTMLElement)) return '';
    const h = el as HTMLElement;
    const ds = h.getAttribute('data-key') || h.getAttribute('data-testid');
    if (ds) return `[data-key="${ds}"]`; 
    const id = h.id;
    if (id) return `#${id}`;
    const name = h.getAttribute('name');
    if (name && h.tagName) return `${h.tagName.toLowerCase()}[name="${name}"]`;
    const role = h.getAttribute('role');
    if (role) return `[role="${role}"]`;
    // fallback: tag + klasy (max 2)
    let classNameStr = '';
    try {
      const className = h.className;
      if (typeof className === 'string') {
        classNameStr = className;
      } else if (className && typeof className === 'object') {
        // Obsługa SVGAnimatedString lub DOMTokenList
        if ('baseVal' in className && typeof (className as any).baseVal === 'string') {
          classNameStr = (className as any).baseVal;
        } else if ('value' in className && typeof (className as any).value === 'string') {
          classNameStr = (className as any).value;
        } else if (Array.isArray(className) || (className as any).length !== undefined) {
          // DOMTokenList
          classNameStr = Array.from(className as any).join(' ');
        } else {
          classNameStr = String(className);
        }
      }
    } catch {
      classNameStr = '';
    }
    const cls = classNameStr.split(/\s+/).filter(Boolean).slice(0,2).map(c=>`.${c}`).join('');
    return `${h.tagName.toLowerCase()}${cls}`;
  }, []);

  const qs = React.useCallback((sel: string): HTMLElement | null => {
    if (!sel) return null;
    try { return document.querySelector(sel) as HTMLElement | null; } catch { return null; }
  }, []);

  // === EMITERY (po stronie obserwowanego użytkownika) ===
  React.useEffect(() => {
    // nie emitujemy, jeśli sami kogoś śledzimy
    if (followUserId) return;
    const s = getSocket();
    const onClick = (e: Event) => {
      const t = e.target as HTMLElement | null;
      const path = getSelector(t);
      // Jeżeli klik dotyczy przejścia do projektu – wyślij specjalny sygnał
      const pid = t?.getAttribute('data-project-id') || t?.closest('[data-project-id]')?.getAttribute('data-project-id');
      if (pid) { try { s.emit('ui:projects:select', { projectId: pid }); } catch {} }
      try { s.emit('dom:click', { path }); } catch {}
    };
    const onInput = (e: Event) => {
      const t = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (!t) return;
      const path = getSelector(t);
      let value: any = (t as any).value;
      if ((t as any).type === 'checkbox') value = (t as any).checked;
      try { s.emit('dom:input', { path, value }); } catch {}
    };
    const onFocus = (e: Event) => { const t = e.target as HTMLElement | null; const path = getSelector(t); try { getSocket().emit('dom:focus', { path }); } catch {} };
    const onBlur  = (e: Event) => { const t = e.target as HTMLElement | null; const path = getSelector(t); try { getSocket().emit('dom:blur',  { path }); } catch {} };
    const onScroll = (e: Event) => {
      const t = e.target as HTMLElement | Document;
      if (t === document) { try { s.emit('dom:scroll', { path: '', top: window.scrollY, left: window.scrollX }); } catch {} return; }
      const el = t as HTMLElement; const path = getSelector(el);
      try { s.emit('dom:scroll', { path, top: el.scrollTop, left: el.scrollLeft }); } catch {}
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('input', onInput, true);
    document.addEventListener('change', onInput, true);
    document.addEventListener('focus', onFocus, true);
    document.addEventListener('blur', onBlur, true);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('input', onInput, true);
      document.removeEventListener('change', onInput, true);
      document.removeEventListener('focus', onFocus, true);
      document.removeEventListener('blur', onBlur, true);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [followUserId, getSelector]);

  // === ODBIORCY (po stronie admina w trybie follow) ===
  React.useEffect(() => {
    if (!followUserId) return;
    const s = getSocket();
    const clickRecv = ({ path }: any) => { const el = qs(path); try { el?.dispatchEvent(new MouseEvent('click', { bubbles: true })); el?.click?.(); } catch {} };
    const inputRecv = ({ path, value }: any) => {
      const el = qs(path) as any;
      if (!el) return;
      try {
        if (el.type === 'checkbox') { el.checked = !!value; el.dispatchEvent(new Event('change', { bubbles: true })); return; }
        el.value = value ?? '';
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      } catch {}
    };
    const focusRecv = ({ path }: any) => { try { qs(path)?.focus(); } catch {} };
    const blurRecv  = ({ path }: any) => { try { qs(path)?.blur(); } catch {} };
    const scrollRecv = ({ path, top, left }: any) => {
      if (!path) { try { window.scrollTo({ top: Number(top)||0, left: Number(left)||0, behavior: 'smooth' }); } catch {} return; }
      const el = qs(path); if (!el) return; try { (el as any).scrollTo?.({ top: Number(top)||0, left: Number(left)||0, behavior: 'smooth' }); (el as any).scrollTop = Number(top)||0; (el as any).scrollLeft = Number(left)||0; } catch {}
    };
    s.on('dom:click', clickRecv);
    s.on('dom:input', inputRecv);
    s.on('dom:focus', focusRecv);
    s.on('dom:blur',  blurRecv);
    s.on('dom:scroll', scrollRecv);
    return () => { s.off('dom:click', clickRecv); s.off('dom:input', inputRecv); s.off('dom:focus', focusRecv); s.off('dom:blur', blurRecv); s.off('dom:scroll', scrollRecv); };
  }, [followUserId, qs]);

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1101 }}>
      {/* Przyciemnienie tła w trybie śledzenia */}
      {followUserId ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.30)' }} />
      ) : null}
      {followUserId ? (
        <div style={{ position: 'fixed', top: 8, right: 8, zIndex: 1200, pointerEvents: 'auto', display: 'flex', gap: 8 }}>
          <button id="cobrowse-exit" onClick={() => { setFollowUserId(null); try { sessionStorage.removeItem('cobrowse:follow:userId'); } catch {} try { window.close(); } catch {} }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            Zakończ śledzenie (Esc)
          </button>
          <button onClick={() => setAssistActive((v)=>!v)} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #7c3aed', background: assistActive ? '#7c3aed' : 'transparent', color: assistActive ? '#fff' : '#7c3aed', fontWeight: 700, cursor: 'pointer' }}>
            Wskaźnik admina: {assistActive ? 'ON' : 'OFF'}
          </button>
        </div>
      ) : null}
      {/* Czerwony banner informacyjny */}
      {followUserId ? (
        <div style={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: '#dc2626', color: '#fff', padding: '10px 16px', borderRadius: 8, boxShadow: '0 2px 10px rgba(0,0,0,0.3)', fontWeight: 700, zIndex: 1200, pointerEvents: 'none' }}>
          Tryb śledzenia – widok tylko do podglądu
        </div>
      ) : null}
      {visibleEntries.map(([id, c]) => (
        <div key={id} style={{ position: 'fixed', left: c.x, top: c.y, transform: 'translate(4px,4px)', color: c.color, fontWeight: 700, fontSize: 12, zIndex: followUserId === id ? 1102 : 1101 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill={c.color} xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }}>
            <path d="M3 2L20 12L13 14L10 21L3 2Z"/>
          </svg>
          <span style={{ marginLeft: 6, background: followUserId === id ? '#0ea5e9' : 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', borderRadius: 6 }}>{c.name}{followUserId === id ? ' (śledzisz)' : ''}</span>
        </div>
      ))}

      {/* Interaktywny overlay dla wskaźnika admina (wysyłka) */}
      {assistActive && followUserId ? (
        <div
          onMouseMove={(e) => { try { const nx = e.clientX / window.innerWidth; const ny = e.clientY / window.innerHeight; getSocket().emit('assist:pointer', { toUserId: followUserId, type: 'move', nx, ny }); } catch {} }}
          onMouseEnter={(e) => { try { const nx = e.clientX / window.innerWidth; const ny = e.clientY / window.innerHeight; getSocket().emit('assist:pointer', { toUserId: followUserId, type: 'on', nx, ny }); } catch {} }}
          onMouseLeave={() => { try { getSocket().emit('assist:pointer', { toUserId: followUserId, type: 'off' }); } catch {} }}
          style={{ position: 'fixed', inset: 0, pointerEvents: 'auto', cursor: 'crosshair', background: 'transparent' }}
        />
      ) : null}

      {/* Odbiór wskaźnika po stronie użytkownika (render) */}
      {remoteAssistPointer?.visible ? (
        <div style={{ position: 'fixed', left: remoteAssistPointer.x, top: remoteAssistPointer.y, transform: 'translate(-50%,-50%)', zIndex: 1300, pointerEvents: 'none' }}>
          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(124,58,237,0.9)', boxShadow: '0 0 0 6px rgba(124,58,237,0.25)' }} />
        </div>
      ) : null}
    </div>
  );
};

export default LiveCursorsOverlay;
